import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EVALUATION_RULE_RESULTS } from '@/entities/expert-report';
import { useSettings } from '@/entities/settings';
import { CheckboxSettingsPopover } from '@/shared/ui';

/** Отметка «Показать все» — не результат правила, поэтому её значение не пересекается с кодами результатов. */
const SHOW_ALL_OPTION = 'all';

/**
 * Кнопка с окном отбора правил заключения по результату.
 *
 * Отбор — личная настройка рабочего места: один на все заключения всех диалогов, хранится
 * за пользователем (`dialogsPage.expertReportRuleResults`). Пустой список — показаны правила
 * всех результатов, то есть отмечено «Показать все».
 */
export const ExpertReportResultFilter: FC = () => {
  const { t } = useTranslation(['dialogs']);
  const { dialogsPage, setDialogsSettings } = useSettings();
  const pickedResults = useMemo(
    () => dialogsPage?.expertReportRuleResults ?? [],
    [dialogsPage?.expertReportRuleResults],
  );

  const options = useMemo(
    () => [
      { value: SHOW_ALL_OPTION, label: t('dialogs:expertReport.resultFilter.all') },
      ...EVALUATION_RULE_RESULTS.map((result) => ({
        value: result,
        label: t(`dialogs:expertReport.resultFilter.${result}` as never),
      })),
    ],
    [t],
  );

  // Отбор, под который не подходит ни один результат, не выставляется, поэтому отмечено всегда
  // либо «Показать все», либо хотя бы один результат
  const values = useMemo(
    () => (pickedResults.length > 0 ? pickedResults : [SHOW_ALL_OPTION]),
    [pickedResults],
  );

  const handleChange = useCallback(
    (nextValues: string[]) => {
      // «Показать все» и отметки результатов взаимно исключают друг друга: отметка «Показать все»
      // снимает результаты, отметка результата — «Показать все» (она просто не попадает в отбор)
      const isShowAllPicked = nextValues.includes(SHOW_ALL_OPTION) && pickedResults.length > 0;
      const nextResults = isShowAllPicked
        ? []
        : EVALUATION_RULE_RESULTS.filter((result) => nextValues.includes(result));
      // Отметка всех результатов, как и снятие последней отметки, равносильна «Показать все»
      const isSameAsShowAll = nextResults.length === EVALUATION_RULE_RESULTS.length;
      setDialogsSettings({ expertReportRuleResults: isSameAsShowAll ? [] : nextResults });
    },
    [pickedResults, setDialogsSettings],
  );

  return (
    <CheckboxSettingsPopover
      title={t('dialogs:expertReport.resultFilter.title')}
      name="expert-report-result-filter"
      options={options}
      value={values}
      onChange={handleChange}
    />
  );
};
